import {
  DECLINE_REASON_LABELS,
  DeclineReason,
  Order,
  Quote,
  formatBRL,
  formatIsoDate,
  type QuoteDeposit,
  depositText,
} from '@deizi/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { Field } from '../../components/Form';
import { Modal, Sheet } from '../../components/Overlays';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { useInvalidateQuotes } from '../../lib/quotes-api';
import { useIsPhone } from '../../lib/use-media';

export type QuoteAction = 'send' | 'accept' | 'expire' | 'reopen';

const DONE: Record<QuoteAction, string> = {
  send: 'marcado como enviado',
  accept: 'marcado como aceito',
  expire: 'marcado como expirado',
  reopen: 'reaberto como rascunho',
};

/** Status actions (`POST /quotes/{id}/send|accept|expire|reopen`) with a toast and refresh. */
export function useQuoteAction() {
  const invalidate = useInvalidateQuotes();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: QuoteAction }) =>
      api.post(`/quotes/${id}/${action}`, Quote, action === 'reopen' ? {} : undefined),
    onSuccess: (q, { action }) => {
      invalidate();
      toast(
        action === 'reopen'
          ? `${q.number} reaberto como rascunho, válido até ${formatIsoDate(q.validUntil)}`
          : `${q.number} ${DONE[action]}`,
      );
    },
    onError: (err) => toast(errorMessage(err)),
  });
}

const REASONS = DeclineReason.options;

/** "Por que recusou?" with the reason chips and an optional note (board Orcamento-Detalhe). */
export function DeclineDialog({
  quote,
  onClose,
}: {
  quote: { id: string; number: string };
  onClose: () => void;
}) {
  const phone = useIsPhone();
  const invalidate = useInvalidateQuotes();
  const toast = useToast();
  const [reason, setReason] = useState<DeclineReason | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const decline = useMutation({
    mutationFn: () =>
      api.post(`/quotes/${quote.id}/decline`, Quote, { reason, note: note.trim() || null }),
    onSuccess: (q) => {
      invalidate();
      toast(`${q.number} marcado como recusado`);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const submit = () => {
    if (!reason) {
      setError('Escolha um motivo. Ele alimenta o relatório de conversão.');
      return;
    }
    decline.mutate();
  };
  const body = (
    <>
      <p className="sm muted">O motivo entra no relatório de conversão (aceitos vs. recusados).</p>
      <div className="row wrap" style={{ gap: 8 }} role="radiogroup" aria-label="Motivo">
        {REASONS.map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={reason === r}
            className={reason === r ? 'chip on' : 'chip'}
            style={phone ? { minHeight: 44 } : undefined}
            onClick={() => {
              setReason(r);
              setError(null);
            }}
          >
            {DECLINE_REASON_LABELS[r]}
          </button>
        ))}
      </div>
      {error ? <p className="help err">{error}</p> : null}
      <Field id="rn" label="Observação (opcional)">
        <textarea
          id="rn"
          className="input"
          placeholder="Ex.: achou caro comparado a troféu de resina"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </>
  );
  const buttons = (
    <>
      <button type="button" className="btn" onClick={onClose}>
        Cancelar
      </button>
      <button type="button" className="btn d" onClick={submit} disabled={decline.isPending}>
        Marcar recusado
      </button>
    </>
  );
  if (phone) {
    return (
      <Sheet label="Por que recusou?" onClose={onClose}>
        <h2>Por que recusou?</h2>
        {body}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>{buttons}</div>
      </Sheet>
    );
  }
  return (
    <Modal title="Por que recusou?" onClose={onClose} footer={buttons}>
      {body}
    </Modal>
  );
}

/**
 * "Criar pedido a partir do orçamento" (board Orcamento-Detalhe): items, prices and frozen costs
 * are copied; the quote becomes Converted.
 */
export function ConvertDialog({
  quote,
  onClose,
}: {
  quote: {
    id: string;
    number: string;
    itemsSummary: string;
    channel: { name: string } | null;
    totals: { priceCentavos: number; costCentavos?: number };
    deposit: QuoteDeposit | null;
  };
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [idem] = useState(newIdempotencyKey);
  const convert = useMutation({
    mutationFn: () => api.post(`/quotes/${quote.id}/convert`, Order, {}, { idempotencyKey: idem }),
    onSuccess: (o) => {
      void qc.invalidateQueries({ queryKey: ['quotes'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast(`${quote.number} virou o pedido ${o.number}`);
      void navigate(`/pedidos/${o.id}`);
    },
  });
  return (
    <Modal
      title="Criar pedido a partir do orçamento"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={convert.isPending}
            onClick={() => convert.mutate()}
          >
            Criar pedido
          </button>
        </>
      }
    >
      <p className="sm">Isto vai ser copiado para o novo pedido:</p>
      <div className="card sunk col" style={{ gap: 2, padding: 12, borderRadius: 12 }}>
        <div className="kv sm">
          <span className="muted">Itens</span>
          <span className="v">{quote.itemsSummary}</span>
        </div>
        <div className="kv sm">
          <span className="muted">Preços</span>
          <span className="v">
            {formatBRL(quote.totals.priceCentavos)}
            {quote.channel ? ` · ${quote.channel.name}` : ''}
          </span>
        </div>
        <div className="kv sm">
          <span className="muted">Custos congelados</span>
          <span className="v">do orçamento</span>
        </div>
        <div className="kv sm">
          <span className="muted">Cliente e observações</span>
          <span className="v">sim</span>
        </div>
        <div className="kv sm">
          <span className="muted">Sinal</span>
          <span className="v">{depositText(quote.deposit)}</span>
        </div>
      </div>
      <p className="xs muted">
        O orçamento fica como Convertido e passa a apontar para o pedido. O que tiver em pronta
        entrega é reservado e o resto entra na fila de impressão.
      </p>
      {convert.isError ? <p className="help err">{errorMessage(convert.error)}</p> : null}
    </Modal>
  );
}
